import { Component, EventEmitter, forwardRef, Input, Output, ViewChild, QueryList, AfterViewInit, ViewChildren } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CrudTableViewConfig, CrudTableViewColumn, CrudViewColumnType } from '../../../utils/list-grid';
import { cloneDeep } from 'lodash';
import { HeadingType } from '../../../utils/heading';
import { InputSize } from '../../../utils/shared';
import { DateFormat } from '../../../utils/date-picker';
import { PropertyType } from '../../../utils/input-template';
import { NzTabSetComponent } from 'ng-zorro-antd/tabs';
import { SelectableOption, SelectableOptionWithTooltip } from '../../../utils/form-utils/selectable-option.type';
import { AlertTypes } from '../../../utils/alert/alert-types';
import { TextComponentConfig } from '../../../utils/text';
import { ActionsInputComponent } from '../actions-input/actions-input.component';
import { TextColorType } from '../../../utils/edit-form/crud-view-text-color-type.enum';
import { TextStyleType } from '../../../utils/edit-form/crud-view-text-style-type.enum';
import { FieldFormattingService } from '../../../utils/field-formatting/field-formatting.service';
import { FieldFormattingContext } from '../../../utils/field-formatting/field-formatting-context.constants';

@Component({
  selector: 'rappider-grid-builder-settings',
  templateUrl: './grid-builder-settings.component.html',
  styleUrls: ['./grid-builder-settings.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GridBuilderSettingsComponent),
      multi: true
    }
  ]
})
export class GridBuilderSettingsComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('gridSettingsTabs') gridSettingsTabs: NzTabSetComponent;
  @ViewChildren(ActionsInputComponent) actionsInputComponents: QueryList<ActionsInputComponent>;

  @Output() ngModelChange = new EventEmitter<CrudTableViewConfig>();
  @Output() columnSaved = new EventEmitter<{ column: CrudTableViewColumn; index: number }>();
  @Output() editJsonData = new EventEmitter<void>();

  CrudViewColumnType = CrudViewColumnType;
  TextColorType = TextColorType;
  TextStyleType = TextStyleType;
  FieldFormattingContext = FieldFormattingContext;
  activeTabIndex = 0;
  tempValueSubmitted = false;
  isFirstInitialize = false;
  currentColumnContext: string | null = null;

  GridBuilderEmptyInfoAlertConfig = {
    type: AlertTypes.Info,
    description: <TextComponentConfig>{
      text: 'Please select a column to edit'
    },
    closeable: false,
    showIcon: true
  };

  typeSelectOptions: SelectableOption[] = Object.entries(CrudViewColumnType).map(([key, value]) => ({ key: key, value: value }));
  inputTemplateTypeSelectOptions: SelectableOption[] = Object.entries(PropertyType).map(([key, value]) => ({ key: key, value: value }));

  @Input() set columnToEdit(value: { column: CrudTableViewColumn; index: number }) {
    if (value) {
      this.editingColumnIndex = value.index;
      this.editingColumn = cloneDeep(value.column);
      this.originalColumn = cloneDeep(value.column);
      this.showColumnErrors = false;
      this.activeTabIndex = 0;
    } else {
      this.clearColumnEdit();
    }
  }

  _value: CrudTableViewConfig;
  editingColumn: CrudTableViewColumn;
  originalColumn: CrudTableViewColumn;
  editingColumnIndex: number;
  showColumnErrors = false;

  headingTypeOptions = Object.entries(HeadingType).map(([key, value]) => ({
    key: key,
    value: value
  }));

  inputSizeOptions = Object.entries(InputSize).map(([key, value]) => ({
    key: key,
    value: value
  }));

  dateFormatOptions = Object.entries(DateFormat).map(([key, value]) => ({
    key: key,
    value: value
  }));

  columnTypeOptions = Object.entries(CrudViewColumnType).map(([key, value]) => ({
    key: key,
    value: value
  }));

  textColorOptions: SelectableOptionWithTooltip[] = [
    { key: 'Default', value: TextColorType.Default },
    { key: 'Warning', value: TextColorType.Warning },
    { key: 'Error', value: TextColorType.Error },
    { key: 'Success', value: TextColorType.Success },
    { key: 'Info', value: TextColorType.Info }
  ];

  styleButtons = [
    { type: TextStyleType.Bold, icon: 'fa-bold', tooltip: 'Bold' },
    { type: TextStyleType.Italic, icon: 'fa-italic', tooltip: 'Italic' },
    { type: TextStyleType.Underline, icon: 'fa-underline', tooltip: 'Underline' }
  ];

  get value() {
    return this._value;
  }

  set value(value: CrudTableViewConfig) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
    this.ngModelChange.emit(value);
  }

  constructor(private fieldFormattingService: FieldFormattingService) { }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: CrudTableViewConfig) {
    if (!value) {
      this._value = {
        columns: [],
        title: { content: '', type: HeadingType.H5 },
        listActions: [],
        itemActions: []
      };
    } else {
      this._value = value;
      if (!this._value.title) {
        this._value.title = { content: '', type: HeadingType.H5 };
      }
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  onItemsChange() {
    this.value = { ...this.value };
  }

  onTitleContentChange(content: string) {
    if (!this.value.title) {
      this.value.title = { content: '', type: HeadingType.H5 };
    }
    this.value.title.content = content;
    this.onItemsChange();
  }

  onTitleTypeChange(type: HeadingType) {
    if (!this.value.title) {
      this.value.title = { content: '', type: HeadingType.H5 };
    }
    this.value.title.type = type;
    this.onItemsChange();
  }

  saveColumn() {
    this.tempValueSubmitted = true;
    if (this.isColumnValid()) {
      this.columnSaved.emit({
        column: this.editingColumn,
        index: this.editingColumnIndex
      });
      if (this._value) {
        this.onChange(this._value);
      }

      this.tempValueSubmitted = false;
    }
  }

  cancelColumnEdit() {
    this.clearColumnEdit();
    this.tempValueSubmitted = false;
  }

  clearColumnEdit() {
    this.editingColumn = null;
    this.originalColumn = null;
    this.editingColumnIndex = null;
    this.showColumnErrors = false;
    this.activeTabIndex = 0;
  }

  isColumnValid(): boolean {
    return (
      this.editingColumn &&
      this.editingColumn.title &&
      this.editingColumn.fieldName &&
      this.isFieldNameUnique()
    );
  }

  isFieldNameUnique(): boolean {
    if (!this.editingColumn || !this.value || !this.value.columns) {
      return true;
    }

    const hasOverlappedFieldName = this.value.columns.some(
      (column, index) =>
        column.fieldName === this.editingColumn.fieldName &&
        index !== this.editingColumnIndex
    );

    return !hasOverlappedFieldName;
  }

  /**
   * Automatically save column when input field loses focus
   */
  onColumnFieldBlur() {
    if (this.editingColumn && this.editingColumn.title && !this.editingColumn.fieldName) {
      this.generateFieldNameFromTitle();
    }

    setTimeout(() => {
      if (this.editingColumn && this.isColumnValid()) {
        this.saveColumn();
      }
    }, 10);
  }

  /**
   * Generate fieldName from title (convert to camelCase)
   */
  generateFieldNameFromTitle() {
    if (!this.editingColumn || !this.editingColumn.title) {
      return;
    }

    const title = this.editingColumn.title.trim();
    const newFieldName = title
      .toLowerCase()
      .replace(/\s+(.)/g, (match, group) => group.toUpperCase())
      .replace(/\s/g, '')
      .replace(/[^a-zA-Z0-9]/g, '');

    const oldFieldName = this.editingColumn.fieldName;
    this.editingColumn.fieldName = newFieldName;

    if (oldFieldName && oldFieldName !== newFieldName && this._value?.visibilityColumns) {
      const visibilityColumns = [...this._value.visibilityColumns];
      const oldIndex = visibilityColumns.indexOf(oldFieldName);
      if (oldIndex !== -1) {
        visibilityColumns[oldIndex] = newFieldName;
        this._value.visibilityColumns = visibilityColumns;
      }
    }
  }

  ngAfterViewInit() {
    if (this.gridSettingsTabs) {
      this.gridSettingsTabs.nzSelectedIndexChange.subscribe((index) => {
        if (this.activeTabIndex === 1 || this.activeTabIndex === 2) {
          this.resetActionsEditStates();
        }
        this.activeTabIndex = index;
      });
    }
  }

  resetActionsEditStates() {
    if (this.actionsInputComponents && this.actionsInputComponents.length > 0) {
      this.actionsInputComponents.forEach(component => {
        if (component.value && component.value.length > 0) {
          component.value.forEach(item => {
            if (item.isEditing) {
              item.isEditing = false;
            }
          });
          component.onChange(component.value);
        }
      });
    }
  }

  /**
   * Set active tab programmatically
   * @param index Tab index to set as active
   */
  setActiveTab(index: number) {
    this.activeTabIndex = index;
    if (this.gridSettingsTabs) {
      this.gridSettingsTabs.nzSelectedIndex = index;
    }
  }

  /**
   * Show validation errors by setting tempValueSubmitted to true
   */
  showValidationErrors() {
    this.tempValueSubmitted = true;
    if (this.actionsInputComponents && this.actionsInputComponents.length > 0) {
      this.actionsInputComponents.forEach(component => {
        component.showValidationErrors();
      });
    }
  }

  resetValidationErrors() {
    this.tempValueSubmitted = false;
    if (this.actionsInputComponents && this.actionsInputComponents.length > 0) {
      this.actionsInputComponents.forEach(component => {
        component.resetValidationErrors();
      });
    }
  }

  onEditJsonData() {
    this.editJsonData.emit();
  }

  /**
   * Track by function for column list
   */
  trackByFieldName(index: number, column: CrudTableViewColumn): string {
    return column.fieldName || `column-${index}`;
  }

  /**
   * Get text color value (delegates to service)
   */
  getTextColorValue(colorType: TextColorType | string | undefined): string {
    return this.fieldFormattingService.getTextColorValue(colorType);
  }

  /**
   * Get column text color
   */
  getColumnTextColor(column: CrudTableViewColumn): TextColorType | string | undefined {
    return column?.textColor;
  }

  /**
   * Get column text style
   */
  getColumnTextStyle(column: CrudTableViewColumn): TextStyleType | undefined {
    return column?.textStyle;
  }

  /**
   * Get column background color
   */
  getColumnBackgroundColor(column: CrudTableViewColumn): TextColorType | string | undefined {
    return column?.backgroundColor;
  }

  /**
   * Check if all columns have the same background color
   */
  hasAllColumnsSameBackgroundColor(): boolean {
    if (!this.value?.columns?.length) {
      return false;
    }
    const firstColor = this.getColumnBackgroundColor(this.value.columns[0]);
    return this.value.columns.every(column => this.getColumnBackgroundColor(column) === firstColor);
  }

  /**
   * Get common background color that all columns share
   */
  getAllColumnsBackgroundColor(): TextColorType | string | undefined {
    if (!this.hasAllColumnsSameBackgroundColor() || !this.value?.columns?.length) {
      return undefined;
    }
    return this.getColumnBackgroundColor(this.value.columns[0]);
  }

  /**
   * Check if all columns have the same text color
   */
  hasAllColumnsSameTextColor(): boolean {
    if (!this.value?.columns?.length) {
      return false;
    }
    const firstColor = this.getColumnTextColor(this.value.columns[0]);
    return this.value.columns.every(column => this.getColumnTextColor(column) === firstColor);
  }

  /**
   * Get common text color that all columns share
   */
  getAllColumnsTextColor(): TextColorType | string | undefined {
    if (!this.hasAllColumnsSameTextColor() || !this.value?.columns?.length) {
      return undefined;
    }
    return this.getColumnTextColor(this.value.columns[0]);
  }

  /**
   * Check if all columns have the same text style
   */
  hasAllColumnsSameTextStyle(): boolean {
    if (!this.value?.columns?.length) {
      return false;
    }
    const firstStyle = this.getColumnTextStyle(this.value.columns[0]);
    return this.value.columns.every(column => this.getColumnTextStyle(column) === firstStyle);
  }

  /**
   * Get common text style that all columns share
   */
  getAllColumnsTextStyle(): TextStyleType | undefined {
    if (!this.hasAllColumnsSameTextStyle() || !this.value?.columns?.length) {
      return undefined;
    }
    return this.getColumnTextStyle(this.value.columns[0]);
  }

  /**
   * Handle column formatting color change
   */
  onColumnFormattingColorChange(context: string, color: TextColorType | string | undefined): void {
    if (!this.value?.columns) {
      return;
    }

    if (context === FieldFormattingContext.ALL_FIELDS) {
      this.applyColorToAllColumns(color);
    } else {
      this.onColumnTextColorChange(context, color);
    }
  }

  /**
   * Handle column formatting background color change
   */
  onColumnFormattingBackgroundColorChange(context: string, color: TextColorType | string | undefined): void {
    if (!this.value?.columns) {
      return;
    }

    if (context === FieldFormattingContext.ALL_FIELDS) {
      this.applyBackgroundColorToAllColumns(color);
    } else {
      this.onColumnBackgroundColorChange(context, color);
    }
  }

  /**
   * Handle column background color change
   */
  onColumnBackgroundColorChange(fieldName: string, color: TextColorType | string | undefined): void {
    if (!this.value?.columns) {
      return;
    }

    const columns = this.value.columns.map(column => {
      if (column.fieldName === fieldName) {
        const updatedColumn = cloneDeep(column);
        if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updatedColumn.backgroundColor = color as any;
        } else {
          delete updatedColumn.backgroundColor;
        }
        return updatedColumn;
      }
      return cloneDeep(column);
    });

    this.value = cloneDeep({ ...this.value, columns });
  }

  /**
   * Apply background color to all columns
   */
  applyBackgroundColorToAllColumns(color: TextColorType | string | undefined): void {
    if (!this.value?.columns) {
      return;
    }

    const columns = this.value.columns.map(column => {
      const updatedColumn = cloneDeep(column);
      if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedColumn.backgroundColor = color as any;
      } else {
        delete updatedColumn.backgroundColor;
      }
      return updatedColumn;
    });

    this.value = cloneDeep({ ...this.value, columns });
  }

  /**
   * Handle column text color change
   */
  onColumnTextColorChange(fieldName: string, color: TextColorType | string | undefined): void {
    if (!this.value?.columns) {
      return;
    }

    const columns = this.value.columns.map(column => {
      if (column.fieldName === fieldName) {
        const updatedColumn = cloneDeep(column);
        if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updatedColumn.textColor = color as any;
        } else {
          delete updatedColumn.textColor;
        }
        return updatedColumn;
      }
      return cloneDeep(column);
    });

    this.value = cloneDeep({ ...this.value, columns });
  }

  /**
   * Apply color to all columns
   */
  applyColorToAllColumns(color: TextColorType | string | undefined): void {
    if (!this.value?.columns) {
      return;
    }

    const columns = this.value.columns.map(column => {
      const updatedColumn = cloneDeep(column);
      if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedColumn.textColor = color as any;
      } else {
        delete updatedColumn.textColor;
      }
      return updatedColumn;
    });

    this.value = cloneDeep({ ...this.value, columns });
  }

  /**
   * Handle column formatting style toggle
   */
  onColumnFormattingStyleToggle(context: string, styleType: TextStyleType): void {
    if (!this.value?.columns) {
      return;
    }

    if (context === FieldFormattingContext.ALL_FIELDS) {
      this.toggleAllColumnsStyle(styleType);
    } else {
      this.toggleColumnStyle(context, styleType);
    }
  }

  /**
   * Toggle column style
   */
  toggleColumnStyle(fieldName: string, styleType: TextStyleType): void {
    if (!this.value?.columns) {
      return;
    }

    const column = this.value.columns.find(col => col.fieldName === fieldName);
    if (!column) {
      return;
    }

    const currentStyle = this.getColumnTextStyle(column);
    const newStyle = this.fieldFormattingService.calculateNewStyle(currentStyle, styleType);

    const columns = this.value.columns.map(col => {
      if (col.fieldName === fieldName) {
        const updatedColumn = cloneDeep(col);
        if (newStyle) {
          updatedColumn.textStyle = newStyle;
        } else {
          delete updatedColumn.textStyle;
        }
        return updatedColumn;
      }
      return cloneDeep(col);
    });

    this.value = cloneDeep({ ...this.value, columns });
  }

  /**
   * Toggle style for all columns
   */
  toggleAllColumnsStyle(styleType: TextStyleType): void {
    if (!this.value?.columns) {
      return;
    }

    const currentStyle = this.hasAllColumnsSameTextStyle() ? this.getAllColumnsTextStyle() : undefined;
    const newStyle = this.fieldFormattingService.calculateNewStyle(currentStyle, styleType);

    const columns = this.value.columns.map(column => {
      const updatedColumn = cloneDeep(column);
      if (newStyle) {
        updatedColumn.textStyle = newStyle;
      } else {
        delete updatedColumn.textStyle;
      }
      return updatedColumn;
    });

    this.value = cloneDeep({ ...this.value, columns });
  }

  /**
   * Set current column context
   */
  setCurrentColumnContext(context: string): void {
    this.currentColumnContext = context;
  }

  /**
   * Check if a color option is selected for columns
   */
  isColumnColorOptionSelected(colorValue: TextColorType): boolean {
    if (!this.currentColumnContext) {
      return false;
    }

    if (this.currentColumnContext === FieldFormattingContext.ALL_FIELDS) {
      return this.hasAllColumnsSameTextColor() && this.getAllColumnsTextColor() === colorValue;
    } else {
      const column = this.value?.columns?.find(col => col.fieldName === this.currentColumnContext);
      return column && this.getColumnTextColor(column) === colorValue;
    }
  }
}
